// Consola de llamada flotante (evolución del CallWidget fijo, petición de Yago).
// Port del modal del CRM (crm/apps/web/src/features/phone/components/CallConsole
// + shared/callConsole/useDraggable) a las convenciones de este SPA: inline
// styles, español, sin Tailwind ni i18n.
//
// Popup flotante ARRASTRABLE por su cabecera que aparece al iniciar una llamada:
//   - identidad del lead (nombre + link a su ficha) y número,
//   - estado en vivo + cronómetro,
//   - controles: colgar / hold / mute / transferir (los mismos del widget),
//   - selector de proveedor,
//   - textarea de notas que, al colgar o con "Guardar nota", crea un comentario
//     de llamada en el lead vía createLeadComment (kind 'llamada') — igual que
//     la consola del CRM guarda las notas de llamada.
//
// Se puede minimizar a una PASTILLA. La posición persiste SOLO durante la sesión
// (useDraggable en memoria; no localStorage). El estado de la llamada vive
// ÍNTEGRAMENTE en PhoneContext (único origen de verdad): esta consola no duplica
// el ciclo de llamada, solo lo renderiza y dispara acciones.

import { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import {
  advisorsAvailability,
  LIVE_STATUS_LABELS,
  PROVIDER_LABELS,
} from "../../api/crmPhone";
import { createLeadComment } from "../../api/crmTimeline";
import {
  AdvisorAvailability,
  PhoneCallLiveStatus,
  PhoneProvider,
} from "../../api/crmTypes";
import { usePhone, ActiveCall } from "../../context/PhoneContext";
import { useDraggable, Pos } from "./useDraggable";

const AVAILABILITY_POLL_MS = 20_000;
const PROVIDERS: PhoneProvider[] = ["cloudtalk", "telnyx"];

// Posición inicial: esquina inferior derecha (donde vivía el widget fijo).
function initialPos(): Pos {
  const w = typeof window !== "undefined" ? window.innerWidth : 1200;
  const h = typeof window !== "undefined" ? window.innerHeight : 800;
  return { x: Math.max(16, w - 340), y: Math.max(16, h - 420) };
}

const LIVE_META: Record<
  PhoneCallLiveStatus,
  { label: string; color: string; bg: string }
> = {
  initiated: { label: LIVE_STATUS_LABELS.initiated, color: "#856404", bg: "#fff3cd" },
  answered: { label: LIVE_STATUS_LABELS.answered, color: "#1a7f37", bg: "#d4edda" },
  completed: { label: LIVE_STATUS_LABELS.completed, color: "#495057", bg: "#e9ecef" },
};

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const btnStyle = (
  variant: "danger" | "neutral" | "primary",
): React.CSSProperties => ({
  border: "1px solid",
  borderColor:
    variant === "danger" ? "#dc3545" : variant === "primary" ? "#0069d9" : "#dee2e6",
  background:
    variant === "danger" ? "#dc3545" : variant === "primary" ? "#0069d9" : "white",
  color: variant === "neutral" ? "#1f2933" : "white",
  borderRadius: "0.4rem",
  padding: "0.3rem 0.7rem",
  fontSize: "0.78rem",
  fontWeight: 600,
  cursor: "pointer",
});

/** Cronómetro aislado: solo este nodo se re-renderiza cada segundo. */
function Duration({ answeredAt }: { answeredAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <span style={{ fontFamily: "monospace", fontWeight: 700 }}>
      {fmtDuration(Math.max(0, Math.floor((now - answeredAt) / 1000)))}
    </span>
  );
}

function advisorStatus(a: AdvisorAvailability): {
  label: string;
  color: string;
  selectable: boolean;
} {
  if (!a.online) return { label: "offline", color: "#adb5bd", selectable: false };
  if (a.callState !== "idle") {
    return { label: "ocupada", color: "#dc3545", selectable: false };
  }
  return { label: "libre", color: "#1a7f37", selectable: true };
}

function TransferPanel({ call }: { call: ActiveCall }) {
  const { transfer, startTransfer, completeTransfer, cancelTransfer } = usePhone();
  const [open, setOpen] = useState(false);
  const [advisors, setAdvisors] = useState<AdvisorAvailability[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || transfer.phase !== "idle") return;
    let cancelled = false;
    const load = () => {
      advisorsAvailability()
        .then((res) => {
          if (!cancelled) {
            setAdvisors(res.items);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(
              err instanceof CrmHttpError
                ? describeCrmError(err)
                : "No se pudo cargar la disponibilidad",
            );
          }
        });
    };
    load();
    const interval = setInterval(load, AVAILABILITY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, transfer.phase]);

  if (transfer.phase === "consulting") {
    return (
      <div
        style={{
          marginTop: "0.5rem",
          padding: "0.5rem",
          background: "#e7f1ff",
          borderRadius: "0.4rem",
          fontSize: "0.78rem",
        }}
      >
        <div style={{ marginBottom: "0.4rem" }}>
          Consultando con <strong>{transfer.targetEmail}</strong> · cliente en
          espera
        </div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          <button
            type="button"
            style={btnStyle("primary")}
            disabled={transfer.pending}
            onClick={() => void completeTransfer()}
          >
            {transfer.pending ? "…" : "Completar transferencia"}
          </button>
          <button
            type="button"
            style={btnStyle("neutral")}
            disabled={transfer.pending}
            onClick={() => void cancelTransfer()}
          >
            Cancelar
          </button>
        </div>
        {transfer.error && (
          <div style={{ color: "#dc3545", marginTop: "0.3rem" }}>{transfer.error}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <button
        type="button"
        style={btnStyle("neutral")}
        disabled={call.live !== "answered" || transfer.pending}
        onClick={() => setOpen((o) => !o)}
      >
        Transferir…
      </button>
      {transfer.error && (
        <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "0.3rem" }}>
          {transfer.error}
        </div>
      )}
      {open && (
        <div
          style={{
            marginTop: "0.4rem",
            maxHeight: "10rem",
            overflowY: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "0.4rem",
          }}
        >
          {error && (
            <div style={{ padding: "0.5rem", color: "#dc3545", fontSize: "0.75rem" }}>
              {error}
            </div>
          )}
          {!error && advisors === null && (
            <div style={{ padding: "0.5rem", fontSize: "0.75rem", color: "#73879C" }}>
              Cargando disponibilidad…
            </div>
          )}
          {!error && advisors?.length === 0 && (
            <div style={{ padding: "0.5rem", fontSize: "0.75rem", color: "#73879C" }}>
              Sin asesores disponibles
            </div>
          )}
          {advisors?.map((a) => {
            const st = advisorStatus(a);
            return (
              <button
                key={a.email}
                type="button"
                disabled={!st.selectable || transfer.pending}
                onClick={() => {
                  setOpen(false);
                  void startTransfer(a.email);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  width: "100%",
                  padding: "0.35rem 0.5rem",
                  border: "none",
                  borderBottom: "1px solid #f1f3f5",
                  background: "white",
                  fontSize: "0.75rem",
                  cursor: st.selectable ? "pointer" : "not-allowed",
                  opacity: st.selectable ? 1 : 0.55,
                  textAlign: "left",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: "0.5rem",
                    height: "0.5rem",
                    borderRadius: "50%",
                    background: st.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {a.name ?? a.email}
                </span>
                <span style={{ color: "#73879C" }}>{st.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Selector de proveedor compacto para dentro de la consola (independiente del
 *  del header). Reusa provider/setProvider de PhoneContext. Deshabilitado con
 *  una llamada viva (cambiar de proveedor a media llamada no tiene sentido). */
function ProviderPicker({ disabled }: { disabled: boolean }) {
  const { provider, setProvider } = usePhone();
  return (
    <div
      role="radiogroup"
      aria-label="Proveedor de telefonía"
      style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
    >
      {PROVIDERS.map((p) => {
        const active = provider === p;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => setProvider(p)}
            style={{
              padding: "0.2rem 0.55rem",
              borderRadius: "999px",
              border: active ? "1px solid #0069d9" : "1px solid #dee2e6",
              background: active ? "#0069d9" : "white",
              color: active ? "white" : "#1f2933",
              fontSize: "0.72rem",
              fontWeight: 600,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {PROVIDER_LABELS[p]}
          </button>
        );
      })}
    </div>
  );
}

/** Caja de notas. Guarda un comentario de llamada (kind 'llamada') en el lead.
 *  Autoguarda al colgar (efecto en el padre) o con el botón "Guardar nota". */
function NotesBox({
  call,
  note,
  onChange,
  saving,
  saved,
  saveError,
  onSave,
}: {
  call: ActiveCall;
  note: string;
  onChange: (v: string) => void;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
  onSave: () => void;
}) {
  const canSave = note.trim().length > 0 && !saving;
  return (
    <div style={{ marginTop: "0.6rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
        }}
      >
        <label
          htmlFor="call-console-note"
          style={{
            fontSize: "0.68rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "#73879C",
          }}
        >
          Nota de la llamada
        </label>
        <span style={{ fontSize: "0.68rem", color: "#73879C" }}>
          {saving ? "Guardando…" : saved ? "Guardada ✓" : ""}
        </span>
      </div>
      <textarea
        id="call-console-note"
        value={note}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Anota lo hablado con ${call.leadName ?? "el lead"}…`}
        rows={3}
        style={{
          width: "100%",
          boxSizing: "border-box",
          resize: "vertical",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          padding: "0.4rem 0.5rem",
          fontSize: "0.8rem",
          fontFamily: "inherit",
          color: "#1f2933",
        }}
      />
      <button
        type="button"
        style={{ ...btnStyle("neutral"), marginTop: "0.35rem" }}
        disabled={!canSave}
        onClick={onSave}
      >
        Guardar nota
      </button>
      {saveError && (
        <div style={{ color: "#dc3545", fontSize: "0.72rem", marginTop: "0.3rem" }}>
          {saveError}
        </div>
      )}
    </div>
  );
}

function CallConsoleInner() {
  const {
    activeCall,
    softphone,
    hangup,
    toggleHold,
    toggleMute,
    dismissCall,
    realtimeOk,
    actionError,
  } = usePhone();

  const { pos, dragHandlers } = useDraggable(initialPos());
  const [minimized, setMinimized] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Refs para el autoguardado-al-colgar sin closures obsoletos.
  const noteRef = useRef(note);
  noteRef.current = note;
  const savedRef = useRef(false); // ya guardamos esta sesión de nota
  const leadForNote = activeCall?.leadUlid ?? null;

  // Reset al cambiar de llamada (por callId, único por sesión de llamada).
  const callId = activeCall?.callId ?? null;
  useEffect(() => {
    setNote("");
    setSaved(false);
    setSaveError(null);
    setMinimized(false);
    savedRef.current = false;
  }, [callId]);

  const persistNote = (leadUlid: string, body: string): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    return createLeadComment(leadUlid, { kind: "llamada", body: body.trim() })
      .then(() => {
        savedRef.current = true;
        setSaved(true);
      })
      .catch((err) => {
        setSaveError(
          err instanceof CrmHttpError
            ? describeCrmError(err)
            : "No se pudo guardar la nota",
        );
      })
      .finally(() => setSaving(false));
  };

  // Autoguardado al COLGAR: cuando la llamada pasa a 'completed', si hay nota sin
  // guardar la persistimos (igual que la consola del CRM al terminar).
  const live = activeCall?.live ?? null;
  useEffect(() => {
    if (live !== "completed") return;
    const body = noteRef.current.trim();
    if (!body || savedRef.current || !leadForNote) return;
    void persistNote(leadForNote, body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, leadForNote]);

  if (!activeCall) return null;

  const meta = LIVE_META[activeCall.live];
  const inCall = activeCall.live !== "completed";
  const providerLabel = PROVIDER_LABELS[activeCall.provider];

  // ── Pastilla minimizada ──────────────────────────────────────────────────
  if (minimized) {
    return (
      <div
        role="dialog"
        aria-label="Llamada en curso (minimizada)"
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          background: "#1f2933",
          color: "white",
          borderRadius: "999px",
          padding: "0.35rem 0.4rem 0.35rem 0.75rem",
          boxShadow: "0 6px 20px rgba(0,0,0,.32)",
          fontSize: "0.8rem",
          maxWidth: "calc(100vw - 16px)",
        }}
      >
        <span
          {...dragHandlers}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            cursor: "grab",
            touchAction: "none",
            userSelect: "none",
            minWidth: 0,
          }}
          title="Arrastrar"
        >
          <span aria-hidden>📞</span>
          <span
            style={{
              maxWidth: "10rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {activeCall.leadName ?? activeCall.phone}
          </span>
          {activeCall.live === "answered" && activeCall.answeredAt && (
            <Duration answeredAt={activeCall.answeredAt} />
          )}
        </span>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          title="Expandir"
          aria-label="Expandir consola de llamada"
          style={{
            border: "none",
            background: "transparent",
            color: "rgba(255,255,255,.8)",
            cursor: "pointer",
            fontSize: "0.85rem",
            padding: "0.15rem 0.35rem",
          }}
        >
          ▢
        </button>
      </div>
    );
  }

  // ── Panel completo ───────────────────────────────────────────────────────
  return (
    <div
      role="dialog"
      aria-label="Consola de llamada"
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        zIndex: 1000,
        width: "21rem",
        maxWidth: "calc(100vw - 16px)",
        maxHeight: "85vh",
        overflowY: "auto",
        background: "white",
        borderRadius: "0.6rem",
        boxShadow: "0 12px 34px rgba(0,0,0,.22)",
        border: "1px solid #dee2e6",
        fontSize: "0.82rem",
        color: "#1f2933",
      }}
    >
      {/* Cabecera: handle de arrastre (los botones quedan fuera del handle para
          que su click no lo capture el drag). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem 0.6rem",
          background: "#f8f9fb",
          borderBottom: "1px solid #eef0f3",
          borderTopLeftRadius: "0.6rem",
          borderTopRightRadius: "0.6rem",
        }}
      >
        <span
          {...dragHandlers}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            flex: 1,
            minWidth: 0,
            cursor: "grab",
            touchAction: "none",
            userSelect: "none",
          }}
          title="Arrastrar la consola"
        >
          <span aria-hidden>📞</span>
          <span
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              padding: "0.15rem 0.5rem",
              borderRadius: "999px",
              background: meta.bg,
              color: meta.color,
            }}
          >
            {activeCall.onHold && activeCall.live === "answered"
              ? "En espera"
              : activeCall.connecting && activeCall.live === "initiated"
                ? "Conectando…"
                : meta.label}
          </span>
          {activeCall.live === "answered" && activeCall.answeredAt && (
            <Duration answeredAt={activeCall.answeredAt} />
          )}
          <span aria-hidden style={{ color: "#adb5bd" }}>
            ⠿
          </span>
        </span>
        <button
          type="button"
          onClick={() => setMinimized(true)}
          title="Minimizar"
          aria-label="Minimizar consola de llamada"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#73879C",
            fontSize: "1rem",
            lineHeight: 1,
            padding: "0 0.2rem",
          }}
        >
          —
        </button>
        <button
          type="button"
          onClick={dismissCall}
          title="Cerrar"
          aria-label="Cerrar consola de llamada"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "#73879C",
            fontSize: "0.9rem",
            padding: "0 0.2rem",
          }}
        >
          ✕
        </button>
      </div>

      <div style={{ padding: "0.7rem 0.85rem" }}>
        {/* Identidad + link a la ficha */}
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
          <strong style={{ fontSize: "0.92rem" }}>
            {activeCall.leadName ?? "(sin nombre)"}
          </strong>
          <span style={{ color: "#73879C" }}>{activeCall.phone}</span>
        </div>
        {activeCall.leadUlid ? (
          <div style={{ marginTop: "0.2rem" }}>
            <Link
              to={`/leads/${activeCall.leadUlid}`}
              style={{ fontSize: "0.75rem", color: "#0069d9", textDecoration: "none" }}
            >
              Ver ficha del lead →
            </Link>
          </div>
        ) : (
          <div style={{ marginTop: "0.2rem", fontSize: "0.72rem", color: "#adb5bd" }}>
            Número sin lead asociado — las notas se guardan en su ficha (aquí no
            hay lead que anotar).
          </div>
        )}

        {/* Controles de la llamada */}
        {inCall && (
          <div
            style={{
              display: "flex",
              gap: "0.4rem",
              marginTop: "0.6rem",
              flexWrap: "wrap",
            }}
          >
            <button type="button" style={btnStyle("danger")} onClick={hangup}>
              Colgar
            </button>
            <button
              type="button"
              style={btnStyle("neutral")}
              disabled={activeCall.live !== "answered"}
              onClick={toggleHold}
            >
              {activeCall.onHold ? "Reanudar" : "En espera"}
            </button>
            {activeCall.provider === "telnyx" && (
              <button
                type="button"
                style={btnStyle("neutral")}
                disabled={activeCall.live !== "answered"}
                onClick={toggleMute}
              >
                {softphone.muted ? "Activar mic" : "Silenciar"}
              </button>
            )}
          </div>
        )}

        {inCall && <TransferPanel call={activeCall} />}

        {/* Notas → comentario de llamada en el lead. Solo si la llamada tiene
            lead: sin lead (dialer libre a un número desconocido) no hay ficha
            donde anotar, así que la caja se oculta. */}
        {leadForNote && (
          <NotesBox
            call={activeCall}
            note={note}
            onChange={(v) => {
              setNote(v);
              setSaved(false);
              savedRef.current = false;
            }}
            saving={saving}
            saved={saved}
            saveError={saveError}
            onSave={() => {
              const body = note.trim();
              if (body && leadForNote) void persistNote(leadForNote, body);
            }}
          />
        )}

        {/* Selector de proveedor (bloqueado con llamada viva) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: "0.7rem",
            paddingTop: "0.55rem",
            borderTop: "1px solid #eef0f3",
          }}
        >
          <span style={{ fontSize: "0.7rem", color: "#73879C" }}>
            Proveedor{activeCall.provider === "telnyx" && softphone.muted ? " · mute" : ""}
          </span>
          <ProviderPicker disabled={inCall} />
        </div>
        <div style={{ fontSize: "0.68rem", color: "#adb5bd", marginTop: "0.25rem" }}>
          {providerLabel}
        </div>

        {actionError && (
          <div style={{ color: "#dc3545", fontSize: "0.75rem", marginTop: "0.4rem" }}>
            {actionError}
          </div>
        )}
        {realtimeOk === false && inCall && (
          <div style={{ color: "#856404", fontSize: "0.72rem", marginTop: "0.4rem" }}>
            Estados en vivo no disponibles (Mercure inaccesible): el estado solo
            avanza con tus acciones.
            {activeCall.provider === "cloudtalk" &&
              " Cuelga o cierra la consola cuando termine la llamada."}
          </div>
        )}
      </div>
    </div>
  );
}

// memo: cuelga del contexto y solo re-renderiza con su estado; el tick del
// cronómetro queda aislado en <Duration>.
const CallConsole = memo(CallConsoleInner);
export default CallConsole;
